import React, { useState } from 'react'
import styled from 'styled-components'
import ReactJson from 'react-json-view'

import { Link } from 'react-router-dom'
import {
  Avatar as MuiAvatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  List,
  ListItem,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { green, orange, red } from '@material-ui/core/colors'
import { Cancel, Edit as EditIcon, CheckCircle } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { Theme } from '@material-ui/core/styles/createMuiTheme'

import { Appointment, DaySpec, SkuThresholdTarget } from 'src/api/models'
import { AppointmentDialog } from 'src/modules/supply_chain/components/AppointmentDialog'
import { AvatarUpload } from 'src/modules/shared/components/AvatarUpload'
import { ImageUpload } from 'src/modules/shared/components/ImageUpload'
import { Image } from 'src/modules/shared/components/Image'

import { Barcode } from '../Barcode'
import { urlFor } from '../../routes'
import { Address } from '../../api/models'

const Avatar = styled(MuiAvatar)`
  background: ${(props) => props.theme.palette.primary.main};
`

const Customer = styled.div`
  display: flex;
  align-items: center;
`

const useStyles = makeStyles((theme: Theme) => ({
  image: {
    display: 'block',
    marginRight: 'auto',
    marginLeft: 'auto',
    height: 'initial',
    width: 'initial',
    maxWidth: '100%',
    color: theme.palette.text.primary,
    transition: 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms',
    boxSizing: 'border-box',
    boxShadow: 'rgba(0, 0, 0, 0.12) 0 1px 6px, rgba(0, 0, 0, 0.12) 0 1px 4px',
    borderRadius: theme.shape.borderRadius,
    zIndex: 5,
    opacity: 1,
  },
}))

interface FieldProps {
  row: any
  value?: any
}

export const LinkedField = (field: React.FC<{ row: any; value: any }>) => {
  const Field = field
  const LinkedField = (props: FieldProps) => {
    const { row, value } = props
    const url = urlFor(row) || '/'
    return (
      <Link to={url}>
        <Field row={row} value={value} />
      </Link>
    )
  }
  return LinkedField
}

export const RelatedLinkedField = (
  field: React.FC<{ row: any; value: any }>,
  displayField?: string
) => {
  const Field = field
  const LinkedField = (props: FieldProps) => {
    const { row, value } = props
    if (!value) {
      return <>- None -</>
    }
    const url = urlFor(value) || '/'
    const displayValue = displayField ? value[displayField] : value
    return (
      <Link to={url}>
        <Field row={row} value={displayValue} />
      </Link>
    )
  }
  return LinkedField
}

type Color = 'primary' | 'secondary' | 'default' | 'error' | 'warning' | 'success' | 'info'

const genericColorMap: { [key: string]: string } = {
  error: red[500],
  warning: orange[500],
  success: green[500],
  info: '#2196f3',
}

export const GenericStatusField = (options: any, colorMap: any) => {
  const MappedStatusField = (props: FieldProps) => {
    const { value } = props
    const color: Color = colorMap[value]
    return <Chip label={options[value]} style={{ background: genericColorMap[color] }} />
  }
  return MappedStatusField
}

export const ObjectField = (fieldName: string, field: React.FC<{ row: any; value: any }>) => {
  const Field = field
  const LinkedField = (props: FieldProps) => {
    const { row, value } = props
    return <Field row={row} value={value[fieldName]} />
  }
  return LinkedField
}

export const ValueField = (props: FieldProps) => {
  const { value } = props
  return (value || value === 0) ? value.toLocaleString() : '- None -'
}

export const EnumField = (options: any) => {
  return (props: FieldProps) => {
    const { value } = props
    return value ? options[value] : '- None -'
  }
}

interface dictionary {
  [key: string]: string
}

const statusDictionary: dictionary = {
  pending: 'Pending',
  received: 'Received',
  processing: 'Processing',
  processed: 'Processed',
  shipped: 'Shipped',
  delivered: 'Delivered',
  canceled: 'Canceled',
}

export const StatusField = (props: FieldProps) => {
  const { value } = props
  if (typeof value === 'string') {
    const formattedValue = statusDictionary[value]
    return formattedValue || value || '- None -'
  }
  return value || '- None -'
}

const minuteToTime = (minute: number) => {
  const hour = Math.floor(minute / 60).toLocaleString('en-US', {
    minimumIntegerDigits: 2,
    useGrouping: false,
  })
  const min = (minute % 60).toLocaleString('en-US', {
    minimumIntegerDigits: 2,
    useGrouping: false,
  })
  return `${hour}:${min}`
}

export const HoursOfOperationField = (defaultHours?: DaySpec) => {
  const HoursOfOperationField = (props: FieldProps) => {
    const { value } = props
    const hours = value || defaultHours
    if (!hours) {
      return <>- None -</>
    }
    if (hours.open === hours.close) {
      return 'Closed'
    }
    const open = minuteToTime(hours.open)
    const close = minuteToTime(hours.close)
    return `${open} - ${close}`
  }
  return HoursOfOperationField
}

export const FlagsField = (props: FieldProps) => {
  const { value } = props

  if (Array.isArray(value)) {
    return value.map((flag) => flag.name).join(', ') || '- None -'
  }
  return value || '- None -'
}

export const BooleanField = (props: FieldProps) => {
  const { value } = props
  return value ? <CheckCircle color="primary" /> : <Cancel color="error" />
}

export const DamageField = (props: FieldProps) => {
  const { value } = props
  return value ? (
    <Tooltip title="Damaged">
      <Cancel color="error" />
    </Tooltip>
  ) : (
    <Tooltip title="Undamaged">
      <CheckCircle color="primary" />
    </Tooltip>
  )
}

export const PercentField = (props: FieldProps) => {
  const { value } = props
  if (value !== undefined && value !== null) {
    return <span>{`%${value * 100}`}</span>
  }
  return <>- None -</>
}

export const NumberField = (props: FieldProps) => {
  const { value } = props
  if (value !== undefined && value !== null) {
    return value.toLocaleString()
  }
  return '- None -'
}

export const SkuUnitField = (props: FieldProps) => {
  const { row, value } = props
  if (value !== undefined && value !== null) {
    return <span>{`${value.toLocaleString()} ${row.unitOfMeasureDisplay}`}</span>
  }
  return <span>- None -</span>
}

export const UnitLocUnitField = (props: FieldProps) => {
  const { row, value } = props
  if (value !== undefined && value !== null) {
    return <span>{`${value.toLocaleString()} ${row.sku?.unitOfMeasureDisplay}`}</span>
  }
  return <span>- None -</span>
}

export const JSONField = (props: FieldProps) => {
  const { value } = props
  if (value) {
    return <ReactJson src={value} collapsed />
  }
  return <>- None -</>
}

interface AddressFieldProps extends FieldProps {
  value?: Address
}

export const AddressField = (props: AddressFieldProps) => {
  const { value } = props
  if (!value) {
    return <span>- None -</span>
  }
  return (
    <>
      {value.company && <Typography>{value.company}</Typography>}
      <Typography>{value.consignee}</Typography>
      {value.phone && <Typography>{value.phone}</Typography>}
      <Typography>{value.address1}</Typography>
      {value.address2 && <Typography>{value.address2}</Typography>}
      {value.address3 && <Typography>{value.address3}</Typography>}
      <Typography>
        {value.city}, {value.state} {value.postalCode}
      </Typography>
    </>
  )
}

export const ImageField = (props: FieldProps) => {
  const { value } = props
  const classes = useStyles()
  if (value) {
    return <img className={classes.image} src={value.url} alt={value.url} />
  }
  return <span className={classes.image}>- None -</span>
}

const editImageFieldCacheKey: { [key: string]: any } = {}
const EditImageFieldCache: { [key: string]: React.FC<FieldProps> } = {}
const defaultEditImageField = () => <></>

interface EditImageFieldProps {
  key: string
  upload: (file: File) => Promise<null>
  refresh: () => void
}

export const EditImageField = (props: EditImageFieldProps) => {
  const { key, upload, refresh } = props
  const keyValue = editImageFieldCacheKey[key]
  if (keyValue === upload) {
    return EditImageFieldCache[key] || defaultEditImageField
  }
  const EditImageField = (props: FieldProps) => {
    const { value } = props
    const [open, setOpen] = useState(false)
    return (
      <>
        <ImageUpload
          open={open}
          upload={upload}
          onSave={refresh}
          handleClose={() => setOpen(false)}
        />
        <Grid container>
          <Grid item>
            <IconButton onClick={() => setOpen(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Grid>
          <Grid item>
            <Image src={value} />
          </Grid>
        </Grid>
      </>
    )
  }
  editImageFieldCacheKey[key] = upload
  EditImageFieldCache[key] = EditImageField
  return EditImageFieldCache[key]
}

export const BarcodeField = (props: FieldProps) => {
  const { value } = props
  if (value) {
    return (
      <div style={{ maxWidth: '150px' }}>
        <Barcode barcode={value} showRawBarcode />
      </div>
    )
  }
  return <span>- None -</span>
}

interface MultiFieldSpec {
  id: string
  label: string
  renderer: React.FC<{ row: any; value: any }>
}

export const MultiField = (fields: MultiFieldSpec[]) => {
  const AggregateField = (props: FieldProps) => {
    const { row } = props
    return (
      <Grid container spacing={2}>
        {fields.map((field) => (
          <Grid item container justify="space-between" alignItems="center" key={field.id}>
            <Grid item xs={6}>
              {field.label}
            </Grid>
            <Grid item xs={6}>
              {field.renderer({ row, value: row[field.id] })}
            </Grid>
          </Grid>
        ))}
      </Grid>
    )
  }
  return AggregateField
}

export const DateField = (props: FieldProps) => {
  const { value } = props
  if (value && value.isValid()) {
    return value.format('L')
  }
  return <span>- None -</span>
}

export const DateTimeField = (props: FieldProps) => {
  const { value } = props
  if (value && value.isValid()) {
    return value.format('LLLL')
  }
  return <span>- None -</span>
}

export const RelatedListValueField = (field: string) => {
  const RelatedListField = (props: FieldProps) => {
    const { value } = props
    if (!value) {
      return <></>
    }
    return (
      <List dense={true}>
        {value.map((obj: any) => {
          const url = urlFor(obj)
          if (url) {
            return (
              <ListItem key={obj.guid} button>
                <Link to={url}>{obj[field]}</Link>
              </ListItem>
            )
          }
          return (
            <ListItem key={obj.guid} button>
              {obj[field]}
            </ListItem>
          )
        })}
      </List>
    )
  }
  return RelatedListField
}

export const UnitInfo = () => {
  const RelatedListField = (props: FieldProps) => {
    const { value } = props
    if (!value) {
      return <></>
    }
    return (
      <List dense={true}>
        {value.map((obj: any) => (
          <ListItem key={obj.guid} button>
            <Typography>
              {obj?.num_units} units of [{obj?.sku?.sku}] {obj?.product?.name}
            </Typography>
          </ListItem>
        ))}
      </List>
    )
  }
  return RelatedListField
}

export const ProfileField = (props: FieldProps) => {
  const { value } = props
  if (!value) {
    return <></>
  }
  return (
    <Customer>
      <Avatar alt={value.fullName} src={value.profileImage} />
      <Box ml={3}>
        {value.fullName}
        <br />
        {value.email}
      </Box>
    </Customer>
  )
}

let editProfileFieldCacheKey: any = null
let EditProfileFieldCache: React.FC<FieldProps> = () => <></>

interface EditProfileFieldProps {
  upload: (file: File) => Promise<null>
  refresh: () => void
}

export const EditProfileField = (props: EditProfileFieldProps) => {
  const { upload, refresh } = props
  if (editProfileFieldCacheKey === upload) {
    return EditProfileFieldCache
  }
  const EditProfileField = (props: FieldProps) => {
    const { value } = props
    const [open, setOpen] = useState(false)
    return (
      <>
        <AvatarUpload
          open={open}
          upload={upload}
          onSave={refresh}
          handleClose={() => setOpen(false)}
        />
        <Grid container>
          <Grid item>
            <IconButton onClick={() => setOpen(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Grid>
          <Grid item>
            <Avatar alt={value} src={value} />
          </Grid>
        </Grid>
      </>
    )
  }
  editProfileFieldCacheKey = upload
  EditProfileFieldCache = EditProfileField
  return EditProfileFieldCache
}

export const RowProfileField = (props: FieldProps) => {
  const { row } = props
  if (!row) {
    return <></>
  }
  return (
    <Customer>
      <Avatar alt={row.fullName} src={row.profileImage} />
      <Box ml={3}>
        {row.fullName}
        <br />
        {row.email}
      </Box>
    </Customer>
  )
}

// TODO: fix these parameters
export const CharInputField = (register: any, errors: any) => {
  const InputField = (props: FieldProps) => {
    const { value } = props
    return (
      <TextField
        defaultValue={value}
        id="name"
        name="name"
        size="small"
        inputRef={register()}
        error={'name' in errors}
        helperText={errors.name && errors.name.message}
      />
    )
  }
  return InputField
}

type Action = (value: any, row: any) => void

export const ActionField = (name: string, action: Action) => {
  const renderer = (props: FieldProps) => {
    const { value, row } = props
    return (
      <Button type="button" variant="contained" color="primary" onClick={() => action(value, row)}>
        {name}
      </Button>
    )
  }
  return renderer
}

export const OrderField = (props: FieldProps) => {
  const { value } = props
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {value?.name || '<No Order>'}
      </Grid>
      <Grid item xs={12}>
        <Chip size="small" label={value?.status || 'No Status'} />
      </Grid>
    </Grid>
  )
}

export const OrderStatusField = (props: FieldProps) => {
  const { value } = props
  return <Chip size="small" label={value} />
}

export const ShipmentStatusField = (props: FieldProps) => {
  const { value } = props
  return <Chip size="small" label={value} />
}

export const TrackingNumberField = (props: FieldProps) => {
  const { row } = props
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {row.carrier?.name || '<No Carrier>'}
      </Grid>
      <Grid item xs={12}>
        {row.trackingNumber || '<No tracking number>'}
      </Grid>
    </Grid>
  )
}

export const LinkedPathField = (props: FieldProps) => {
  const { value } = props
  if (value?.length) {
    const items = value.reduce((r: any[], a: any) => r.concat(a, ' > '), [])
    items.pop()
    return items.map((item: any) => {
      const url = urlFor(item) || ''
      if (url) {
        return (
          <Link key={item.guid} to={url}>
            {item.name}
          </Link>
        )
      } else {
        return <span key={item.guid}>{item.name ? item.name : item}</span>
      }
    })
  }
  return '- None -'
}

export const PalletASNField = (props: FieldProps) => {
  const { value } = props
  if (!value) {
    return <span>- None -</span>
  }
  return (
    <Card>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="baseline"
              justifyContent="space-between"
              style={{ width: '100%' }}
            >
              <Typography>Tracking Number: {value.trackingNumber || '- None -'}</Typography>
              <Typography>Bill of Lading Number: {value.billOfLadingNumber || '- None -'}</Typography>
            </Box>
          </Grid>
          {value.customFields &&
            Object.keys(value.customFields).map((label) => (
              <Grid item xs={12} key={label}>
                <Typography>
                  {label}: {value.customFields[label] || '- None -'}
                </Typography>
              </Grid>
            ))}
        </Grid>
      </CardContent>
    </Card>
  )
}

export const FulfillmentStatusField = (props: FieldProps) => {
  const { row, value } = props
  let color: any = red
  if (row.numUnits === row.numFulfilled + row.numPicked) {
    color = green
  } else if (row.numFulfilled > 0 || row.numPicked) {
    color = orange
  }
  return <Chip size="small" label={value} style={{ backgroundColor: color[500], color: 'white' }} />
}

export const ReceiveStatusField = (props: FieldProps) => {
  const { row } = props
  let color: any = red
  if (row.numUnits === row.numReceived) {
    color = green
  } else if (row.numReceived > 0) {
    color = orange
  }
  return (
    <Chip
      size="small"
      label={`${row.numReceived}/${row.numUnits} received`}
      style={{ backgroundColor: color[500], color: 'white' }}
    />
  )
}

export const CustomField = (props: FieldProps) => {
  const { value } = props
  if (!value) {
    return <>- None -</>
  }
  return (
    <Grid container>
      {value.map((obj: any) => (
        <Grid key={obj.guid} item xs={12}>
          <Grid container>
            <Grid item>
              <Typography>{`${obj?.label}: `}</Typography>
              <Typography>{obj?.value}</Typography>
            </Grid>
          </Grid>
        </Grid>
      ))}
    </Grid>
  )
}

export const SkuHealthField = (props: FieldProps) => {
  const { row } = props
  if (row.storageHealth !== 0) {
    return (
      <Tooltip title={row.storageHealth} placement="right">
        <Cancel color="error" />
      </Tooltip>
    )
  }
  return <CheckCircle color="primary" />
}

export const AppointmentField = (props: FieldProps) => {
  const { value } = props
  if (!value) {
    return <>- None -</>
  }
  const hours = parseInt(value.end.diff(value.start, 'hour'))
  const minutes = parseInt(value.end.diff(value.start, 'minute')) % 60
  const url = urlFor(value) || '/'
  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Link to={url}>
            {Appointment.AppointmentType[value.appointmentType as 'live' | 'drop']}
          </Link>
        </Grid>
        <Grid item xs={12}>
          {value.start.format('LLLL')}
        </Grid>
        <Grid item xs={12}>
          For {hours} hour{hours !== 1 ? 's' : ''} {minutes} minute
          {minutes !== 1 ? 's' : ''}
        </Grid>
        {value.broker && (
          <Grid item xs={12}>
            Brokered by {value.broker.name}
          </Grid>
        )}
        {value.dock && (
          <Grid item xs={12}>
            Assgined to Dock {value.dock.name}
          </Grid>
        )}
        {value.driver && (
          <Grid item xs={12}>
            Driven by {value.driver.name}
          </Grid>
        )}
      </Grid>
    </>
  )
}

interface AppointmentFieldProps {
  refresh: () => void
}

export const ASNAppointmentField = (props: AppointmentFieldProps) => {
  const { refresh } = props
  const Field = (props: FieldProps) => {
    const { row, value } = props
    const [open, setOpen] = useState(false)
    const [editOpen, setEditOpen] = useState(false)
    if (!value) {
      return (
        <>
          {open && (
            <AppointmentDialog
              open={open}
              asn={row}
              handleClose={() => setOpen(false)}
              onSave={refresh}
            />
          )}
          <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
            Schedule
          </Button>
        </>
      )
    }
    const hours = parseInt(value.end.diff(value.start, 'hour'))
    const minutes = parseInt(value.end.diff(value.start, 'minute')) % 60
    const url = urlFor(value) || '/'
    return (
      <>
        {editOpen && (
          <AppointmentDialog
            open={editOpen}
            appointment={value}
            update
            asn={row}
            handleClose={() => setEditOpen(false)}
            onSave={refresh}
          />
        )}
        <Grid container>
          <Grid item xs={12}>
            <IconButton onClick={() => setEditOpen(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <Link to={url}>
              {Appointment.AppointmentType[value.appointmentType as 'live' | 'drop']}
            </Link>
          </Grid>
          <Grid item xs={12}>
            {value.start.format('LLLL')}
          </Grid>
          <Grid item xs={12}>
            For {hours} hour{hours !== 1 ? 's' : ''} {minutes} minute
            {minutes !== 1 ? 's' : ''}
          </Grid>
          {value.broker && (
            <Grid item xs={12}>
              Brokered by {value.broker.name}
            </Grid>
          )}
          {value.dock && (
            <Grid item xs={12}>
              Assgined to Dock {value.dock.name}
            </Grid>
          )}
          {value.driver && (
            <Grid item xs={12}>
              Driven by {value.driver.name}
            </Grid>
          )}
        </Grid>
      </>
    )
  }
  return Field
}

export const TruckloadAppointmentField = (props: AppointmentFieldProps) => {
  const { refresh } = props
  const Field = (props: FieldProps) => {
    const { row, value } = props
    const [open, setOpen] = useState(false)
    const [editOpen, setEditOpen] = useState(false)
    if (!value) {
      return (
        <>
          {open && (
            <AppointmentDialog
              open={open}
              truckload={row}
              handleClose={() => setOpen(false)}
              onSave={refresh}
            />
          )}
          <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
            Schedule
          </Button>
        </>
      )
    }
    const hours = parseInt(value.end.diff(value.start, 'hour'))
    const minutes = parseInt(value.end.diff(value.start, 'minute')) % 60
    const url = urlFor(value) || '/'
    return (
      <>
        {editOpen && (
          <AppointmentDialog
            open={editOpen}
            appointment={value}
            update
            truckload={row}
            handleClose={() => setEditOpen(false)}
            onSave={refresh}
          />
        )}
        <Grid container>
          <Grid item xs={12}>
            <IconButton onClick={() => setEditOpen(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <Link to={url}>
              {Appointment.AppointmentType[value.appointmentType as 'live' | 'drop']}
            </Link>
          </Grid>
          <Grid item xs={12}>
            {value.start.format('LLLL')}
          </Grid>
          <Grid item xs={12}>
            For {hours} hour{hours !== 1 ? 's' : ''} {minutes} minute
            {minutes !== 1 ? 's' : ''}
          </Grid>
          {value.broker && (
            <Grid item xs={12}>
              Brokered by {value.broker.name}
            </Grid>
          )}
          {value.dock && (
            <Grid item xs={12}>
              Assgined to Dock {value.dock.name}
            </Grid>
          )}
          {value.driver && (
            <Grid item xs={12}>
              Driven by {value.driver.name}
            </Grid>
          )}
        </Grid>
      </>
    )
  }
  return Field
}

export const SkuThresholdTargetField = (props: FieldProps) => {
  const { value } = props
  if (!value) {
    return <>'- None -'</>
  }
  return <>{SkuThresholdTarget[value as 'tot' | 'fac']}</>
}
